import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from './actions';
import {
  map,
  switchMap,
  catchError,
  filter,
  withLatestFrom,
  mergeMap
} from 'rxjs/operators';
import { getDeal, SharedState, getWiresStoreState } from '../reducer';
import { WiresState } from './state';
import { WiresService } from '../../services/wires.service';
import { of } from 'rxjs';
import { LoadSuccess, DealActionTypes } from '../deal/actions';
import { DealVersion } from '../../types/deal.types';
import { getCurrentDealVersionDistinct } from '../reducer';
import { RouterNavigationAction, ROUTER_NAVIGATION } from '@ngrx/router-store';

@Injectable()
export class WiresEffects {
  public loadWires$ = createEffect(() =>
    this.actions$.pipe(
    ofType(DealActionTypes.LoadSuccess),
    withLatestFrom(this.store.pipe(getCurrentDealVersionDistinct)),
    switchMap(([, deal]: [LoadSuccess, DealVersion]) => {
      return this.wiresService.loadWires(deal.id).pipe(
        map((res) => new actions.LoadWiresListSuccess(res)),
        catchError((err) => of(new actions.LoadWiresListError(err)))
      );
    })
  ));

  public reloadWires$ = createEffect(() =>
    this.actions$.pipe(
    ofType(ROUTER_NAVIGATION),
    filter(
      (navAction: RouterNavigationAction) =>
        !!navAction.payload.routerState.url
          .split('/')
          .find((nav) => nav === 'wires')
    ),
    withLatestFrom(this.store.pipe(getCurrentDealVersionDistinct)),
    switchMap(([, deal]: [unknown, DealVersion]) => {
      return this.wiresService.loadWires(deal.id).pipe(
        map((res) => new actions.LoadWiresListSuccess(res)),
        catchError((err) => of(new actions.LoadWiresListError(err)))
      );
    })
  ));

  public loadWiresDealId$ = createEffect(() =>
    this.actions$.pipe(
    ofType(DealActionTypes.LoadSuccess),
    withLatestFrom(this.store.pipe(getCurrentDealVersionDistinct)),
    map(
      ([, deal]: [LoadSuccess, DealVersion]) =>
        new actions.LoadWiresList(deal.id)
    )
  ));

  public loadFirstWire$ = createEffect(() =>
    this.actions$.pipe(
    ofType(actions.LOAD_WIRES_LIST_SUCCESS),
    withLatestFrom(this.store.pipe(select(getWiresStoreState))),
    map(([action, wiresState]: [actions.LoadWiresListSuccess, WiresState]) => {
      if (action.payload.length > 0) {
        const validWireIsSelected =
          wiresState.wire &&
          action.payload
            .map((wire) => wire.wireId)
            .indexOf(wiresState.wire.wireId) >= 0;

        if (validWireIsSelected) {
          return { type: 'NO_ACTION' };
        } else {
          return new actions.LoadWireContent(wiresState.wires[0].wireId);
        }
      } else {
        return new actions.EmptyWireContent();
      }
    })
  ));

  public loadWire$ = createEffect(() =>
    this.actions$.pipe(
    ofType(actions.LOAD_WIRE_CONTENT),
    withLatestFrom(
      this.store.pipe(
        select(getDeal),
        filter((deal) => !!deal)
      )
    ),
    switchMap(([action, deal]) =>
      this.wiresService
        .loadWire((action as actions.LoadWireContent).payload, deal.id)
        .pipe(
          map((res) => new actions.LoadWireContentSuccess(res)),
          catchError((err) => of(new actions.LoadWireContentError(err)))
        )
    )
  ));

  public printAllWires$ = createEffect(() =>
    this.actions$.pipe(
    ofType(actions.INITIATE_PRINT_ALL_WIRES),
    withLatestFrom(
      this.store.pipe(
        select(getDeal),
        filter((deal) => !!deal)
      ),
      this.store.pipe(select(getWiresStoreState))
    ),
    mergeMap(([, deal, wireState]) => {
      const requests = wireState.wires.map(({ wireId }) => this.wiresService
        .loadWire(wireId, deal.id).toPromise());

      return Promise.all(requests)
        .then(wires => new actions.PrintAllWires(wires.filter(Boolean)))
        .catch(_ => new actions.PrintAllWires([]));
    })
  ));

  constructor(
    private wiresService: WiresService,
    private actions$: Actions,
    private store: Store<SharedState>
  ) { }
}
